// PaperPilot Command Palette (⌘K) — global semantic launcher
// Searches papers, projects, wiki pages, screens, and actions in one bar.
// Keyboard: ⌘K open, ↑↓ navigate, ↵ run, Esc close.

const PALETTE_DATA = {
  ko: {
    placeholder: '이동, 검색, 명령…',
    sectionScreens: '빠른 이동',
    sectionPapers: '논문',
    sectionProjects: '프로젝트',
    sectionWiki: '위키',
    sectionAi: 'AI 명령',
    sectionPrefs: '환경설정',
    empty: '결과가 없어요. 다른 키워드로 시도해보세요.',
    hintNav: '이동',
    hintRun: '선택',
    hintClose: '닫기',
    hintTab: '제안 전환',
    askPrefix: '서재 전체에 묻기',
    newNote: '새 노트',
    newProject: '새 프로젝트',
    newWiki: '새 위키 페이지',
    openPdf: 'PDF 가져오기',
    themeSwitch: '테마 전환…',
    langSwitch: '언어 전환',
    title: '커맨드 팔레트',
  },
  en: {
    placeholder: 'Jump, search, command…',
    sectionScreens: 'Quick navigation',
    sectionPapers: 'Papers',
    sectionProjects: 'Projects',
    sectionWiki: 'Wiki',
    sectionAi: 'AI commands',
    sectionPrefs: 'Preferences',
    empty: 'No results. Try a different keyword.',
    hintNav: 'navigate',
    hintRun: 'run',
    hintClose: 'close',
    hintTab: 'switch suggestion',
    askPrefix: 'Ask across library',
    newNote: 'New note',
    newProject: 'New project',
    newWiki: 'New wiki page',
    openPdf: 'Import PDF',
    themeSwitch: 'Switch theme…',
    langSwitch: 'Switch language',
    title: 'Command palette',
  },
};

const SCREEN_TARGETS_KO = [
  { id: 'dashboard', label: '대시보드', icon: 'dashboard', shortcut: 'G D' },
  { id: 'explore',   label: '논문 탐색', icon: 'compass',  shortcut: 'G E' },
  { id: 'library',   label: '내 서재',   icon: 'library',  shortcut: 'G L' },
  { id: 'bench',     label: '벤치마크 메모리', icon: 'beaker' },
  { id: 'projects',  label: '프로젝트',  icon: 'kanban',   shortcut: 'G P' },
  { id: 'graph',     label: '지식 그래프', icon: 'graph' },
  { id: 'wiki',      label: '연구 위키', icon: 'book',     shortcut: 'G W' },
  { id: 'reader',    label: 'PDF 리더',  icon: 'figure' },
  { id: 'assistant', label: 'AI 어시스턴트', icon: 'bot',  shortcut: '⌘ J' },
];
const SCREEN_TARGETS_EN = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', shortcut: 'G D' },
  { id: 'explore',   label: 'Explore',   icon: 'compass',   shortcut: 'G E' },
  { id: 'library',   label: 'Library',   icon: 'library',   shortcut: 'G L' },
  { id: 'bench',     label: 'Benchmark Memory', icon: 'beaker' },
  { id: 'projects',  label: 'Projects',  icon: 'kanban',    shortcut: 'G P' },
  { id: 'graph',     label: 'Knowledge Graph', icon: 'graph' },
  { id: 'wiki',      label: 'Research Wiki', icon: 'book',  shortcut: 'G W' },
  { id: 'reader',    label: 'PDF Reader', icon: 'figure' },
  { id: 'assistant', label: 'AI Assistant', icon: 'bot',    shortcut: '⌘ J' },
];

const WIKI_PAGES_KO = [
  { id: 'def',    label: 'Deformation Field',  meta: '4DGS · 14편' },
  { id: 'nerf',   label: 'NeRF',               meta: '3D · 18편' },
  { id: '3dgs',   label: '3DGS',               meta: '3D · 22편' },
  { id: '4dgs',   label: '4DGS',               meta: '3D · 14편' },
  { id: 'vla',    label: 'VLA',                meta: '로보틱스 · 33편' },
  { id: 'sfm',    label: 'SfM',                meta: '3D · 8편' },
  { id: 'foundm', label: 'Robot Foundation Model', meta: '로보틱스 · 27편' },
  { id: 'sparse', label: 'Sparse-view Reconstruction', meta: '3D · 11편' },
];
const WIKI_PAGES_EN = [
  { id: 'def',    label: 'Deformation Field',  meta: '4DGS · 14 papers' },
  { id: 'nerf',   label: 'NeRF',               meta: '3D · 18 papers' },
  { id: '3dgs',   label: '3DGS',               meta: '3D · 22 papers' },
  { id: '4dgs',   label: '4DGS',               meta: '3D · 14 papers' },
  { id: 'vla',    label: 'VLA',                meta: 'Robotics · 33 papers' },
  { id: 'sfm',    label: 'SfM',                meta: '3D · 8 papers' },
  { id: 'foundm', label: 'Robot Foundation Model', meta: 'Robotics · 27 papers' },
  { id: 'sparse', label: 'Sparse-view Reconstruction', meta: '3D · 11 papers' },
];

function CommandPalette() {
  const { lang } = useT();
  const navCtx = React.useContext(NavContext);
  const dict = PALETTE_DATA[lang] || PALETTE_DATA.ko;
  const screens = lang === 'ko' ? SCREEN_TARGETS_KO : SCREEN_TARGETS_EN;
  const wikiPages = lang === 'ko' ? WIKI_PAGES_KO : WIKI_PAGES_EN;

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeIdx, setActiveIdx] = React.useState(0);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  // Open / close via ⌘K, /, Esc
  React.useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const metaKey = isMac ? e.metaKey : e.ctrlKey;
      if (metaKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
        return;
      }
      // "/" to open when not focused in an input
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA' && !open) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus input on open, reset query/idx
  React.useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Build result list, grouped
  const groups = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (s) => !q || s.toLowerCase().includes(q);

    // Build sections
    const out = [];

    // 1. Quick navigation
    const navItems = screens
      .filter(s => match(s.label))
      .map(s => ({ kind: 'screen', id: s.id, label: s.label, icon: s.icon, shortcut: s.shortcut, meta: lang === 'ko' ? '화면' : 'screen' }));
    if (navItems.length) out.push({ id: 'screens', label: dict.sectionScreens, items: navItems });

    // 2. Papers (search by title)
    const paperItems = (typeof PAPERS !== 'undefined' ? PAPERS : [])
      .filter(p => match(p.title) || p.tags?.some(t => match(t)))
      .slice(0, 6)
      .map(p => ({
        kind: 'paper',
        id: p.id,
        label: p.title,
        icon: 'figure',
        meta: `${p.venue} · ${p.date}`,
        chips: p.tags?.slice(0, 2),
      }));
    if (paperItems.length) out.push({ id: 'papers', label: dict.sectionPapers, items: paperItems });

    // 3. Projects
    const projects = (typeof PROJECTS !== 'undefined' ? PROJECTS : []);
    const projItems = projects
      .filter(p => match(lang === 'ko' ? p.labelKo : p.labelEn))
      .map(p => ({
        kind: 'project', id: p.id,
        label: lang === 'ko' ? p.labelKo : p.labelEn,
        icon: 'kanban',
        meta: `${p.count} ${lang === 'ko' ? '편' : 'papers'}`,
        accent: p.color,
      }));
    if (projItems.length) out.push({ id: 'projects', label: dict.sectionProjects, items: projItems });

    // 4. Wiki pages
    const wikiItems = wikiPages
      .filter(w => match(w.label))
      .map(w => ({ kind: 'wiki', id: w.id, label: w.label, icon: 'book', meta: w.meta }));
    if (wikiItems.length) out.push({ id: 'wiki', label: dict.sectionWiki, items: wikiItems });

    // 5. AI commands — always show, with query as the question
    const aiItems = [];
    if (q) {
      aiItems.push({
        kind: 'ai-ask', id: 'ask', label: `"${query}" — ${dict.askPrefix}`,
        icon: 'sparkles', meta: lang === 'ko' ? 'AI 질문 · ⌘ ↵' : 'Ask AI · ⌘ ↵',
        highlight: true,
      });
    }
    aiItems.push({ kind: 'ai', id: 'compare-vla', label: lang === 'ko' ? 'VLA 논문 ALOHA 결과 비교' : 'Compare VLA papers on ALOHA', icon: 'sparkles', meta: lang === 'ko' ? '추천 질문' : 'suggested' });
    aiItems.push({ kind: 'ai', id: 'sum-week', label: lang === 'ko' ? '이번 주 4DGS 논문 요약' : "Summarize this week's 4DGS papers", icon: 'sparkles', meta: lang === 'ko' ? '추천 질문' : 'suggested' });
    if (q || aiItems.length > 0) out.push({ id: 'ai', label: dict.sectionAi, items: aiItems.filter(a => match(a.label)) });

    // 6. Actions
    const actionItems = [
      { kind: 'action', id: 'new-note',    label: dict.newNote,    icon: 'comment', shortcut: '⌘ N' },
      { kind: 'action', id: 'new-project', label: dict.newProject, icon: 'plus' },
      { kind: 'action', id: 'new-wiki',    label: dict.newWiki,    icon: 'book' },
      { kind: 'action', id: 'import-pdf',  label: dict.openPdf,    icon: 'paperclip' },
    ].filter(a => match(a.label));
    if (actionItems.length) out.push({ id: 'actions', label: dict.sectionPrefs, items: actionItems });

    return out.filter(g => g.items.length > 0);
  }, [query, lang]);

  const flat = React.useMemo(() => groups.flatMap(g => g.items.map(it => ({ ...it, group: g.id }))), [groups]);

  React.useEffect(() => { setActiveIdx(i => Math.min(i, Math.max(0, flat.length - 1))); }, [flat]);

  const runItem = (item) => {
    if (!item) { setOpen(false); return; }
    if (item.kind === 'screen' && navCtx.navigate) navCtx.navigate(item.id);
    else if (item.kind === 'paper' && navCtx.navigate) navCtx.navigate('detail');
    else if (item.kind === 'project' && navCtx.navigate) navCtx.navigate('projects');
    else if (item.kind === 'wiki' && navCtx.navigate) navCtx.navigate('wiki');
    else if (item.kind === 'ai-ask' || item.kind === 'ai') { if (navCtx.navigate) navCtx.navigate('assistant'); }
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      runItem(flat[activeIdx]);
    }
  };

  // Scroll active into view
  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-cp-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  if (!open) return null;

  let runningIdx = 0;
  return (
    <div className="pp-cp-overlay" onClick={() => setOpen(false)}>
      <div className="pp-cp-modal" onClick={e => e.stopPropagation()}>
        <div className="pp-cp-search">
          <I name="search" size={16} style={{ color: 'var(--fg-2)' }}/>
          <input
            ref={inputRef}
            className="pp-cp-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={dict.placeholder}
            spellCheck={false}
            autoComplete="off"
          />
          <span className="pp-kbd pp-cp-esc">esc</span>
        </div>

        <div className="pp-cp-list scroll" ref={listRef}>
          {flat.length === 0 && (
            <div className="pp-cp-empty">
              <I name="search" size={20} style={{ color: 'var(--fg-3)', marginBottom: 8 }}/>
              <div style={{ color: 'var(--fg-2)', fontSize: 12 }}>{dict.empty}</div>
            </div>
          )}
          {groups.map(group => (
            <div key={group.id} className="pp-cp-group">
              <div className="pp-cp-group-head">{group.label}</div>
              {group.items.map(item => {
                const idx = runningIdx++;
                const isActive = idx === activeIdx;
                return (
                  <div
                    key={`${group.id}-${item.id}-${idx}`}
                    data-cp-idx={idx}
                    className={'pp-cp-row' + (isActive ? ' is-active' : '') + (item.highlight ? ' is-highlight' : '')}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => runItem(item)}
                  >
                    <div className="pp-cp-icon" style={item.accent ? { background: `${item.accent}22`, color: item.accent, borderColor: `${item.accent}55` } : null}>
                      <I name={item.icon} size={13}/>
                    </div>
                    <div className="pp-cp-content">
                      <div className="pp-cp-label ellipsis">{item.label}</div>
                      {item.meta && <div className="pp-cp-meta ellipsis">{item.meta}</div>}
                    </div>
                    {item.chips && (
                      <div className="row gap-1" style={{ flexShrink: 0 }}>
                        {item.chips.map(c => <span key={c} className="tag indigo" style={{ height: 16, fontSize: 9 }}>{c}</span>)}
                      </div>
                    )}
                    {item.shortcut && <span className="pp-kbd pp-cp-shortcut">{item.shortcut}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="pp-cp-footer">
          <div className="pp-cp-hint"><span className="pp-kbd">↑↓</span> <span>{dict.hintNav}</span></div>
          <div className="pp-cp-hint"><span className="pp-kbd">↵</span> <span>{dict.hintRun}</span></div>
          <div className="pp-cp-hint"><span className="pp-kbd">esc</span> <span>{dict.hintClose}</span></div>
          <div style={{ flex: 1 }}/>
          <div className="pp-cp-brand">
            <I name="sparkles" size={11} style={{ color: 'var(--acc-violet)' }}/>
            <span style={{ color: 'var(--fg-3)', fontSize: 10.5 }}>{dict.title}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// styles
if (typeof document !== 'undefined' && !document.getElementById('pp-cp-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-cp-styles';
  s.textContent = `
  .pp-cp-overlay {
    position: fixed; inset: 0;
    z-index: 9999;
    background: rgba(7,8,11,0.55);
    backdrop-filter: blur(10px) saturate(120%);
    display: flex; align-items: flex-start; justify-content: center;
    padding-top: 12vh;
    animation: pp-cp-fade 120ms ease-out;
    font-family: var(--font-sans);
  }
  @keyframes pp-cp-fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes pp-cp-pop { from { opacity: 0; transform: translateY(-6px) scale(0.98); } to { opacity: 1; transform: none; } }

  .pp-cp-modal {
    width: 640px; max-width: calc(100vw - 32px);
    max-height: 70vh;
    display: flex; flex-direction: column;
    background: var(--bg-1);
    border: 1px solid var(--border-2);
    border-radius: 14px;
    box-shadow:
      0 32px 80px rgba(0,0,0,0.55),
      0 0 0 1px rgba(255,255,255,0.04) inset,
      0 0 40px rgba(136,150,255,0.10);
    overflow: hidden;
    animation: pp-cp-pop 140ms cubic-bezier(0.2,0.7,0.3,1);
  }

  .pp-cp-search {
    display: flex; align-items: center; gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border-1);
  }
  .pp-cp-input {
    flex: 1;
    font-family: var(--font-sans);
    font-size: 16px;
    color: var(--fg-0);
    background: transparent;
    border: 0; outline: 0;
    letter-spacing: -0.01em;
  }
  .pp-cp-input::placeholder { color: var(--fg-3); }
  .pp-cp-esc {
    font-size: 9.5px !important;
    color: var(--fg-3);
  }

  .pp-cp-list {
    flex: 1; min-height: 0;
    overflow-y: auto;
    padding: 8px;
  }
  .pp-cp-group { display: flex; flex-direction: column; margin-bottom: 6px; }
  .pp-cp-group-head {
    font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--fg-3);
    padding: 8px 10px 4px;
  }
  .pp-cp-row {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px;
    border-radius: 7px;
    cursor: pointer;
  }
  .pp-cp-row.is-active {
    background: rgba(136,150,255,0.10);
    box-shadow: 0 0 0 1px rgba(136,150,255,0.25) inset;
  }
  .pp-cp-row.is-highlight .pp-cp-icon {
    background: rgba(180,155,255,0.16);
    color: var(--acc-violet);
    border-color: rgba(180,155,255,0.40);
  }
  .pp-cp-row.is-active.is-highlight {
    background: rgba(180,155,255,0.10);
    box-shadow: 0 0 0 1px rgba(180,155,255,0.30) inset;
  }
  .pp-cp-icon {
    width: 26px; height: 26px;
    border-radius: 6px;
    background: var(--bg-3);
    border: 1px solid var(--border-1);
    color: var(--fg-1);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .pp-cp-row.is-active .pp-cp-icon { color: var(--acc-indigo); border-color: rgba(136,150,255,0.40); background: rgba(136,150,255,0.08); }
  .pp-cp-content { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .pp-cp-label { font-size: 13px; color: var(--fg-0); }
  .pp-cp-meta { font-size: 10.5px; color: var(--fg-3); font-family: var(--font-mono); }
  .pp-cp-shortcut {
    font-size: 9.5px !important;
    color: var(--fg-2);
  }
  .pp-cp-empty {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 48px 20px;
    color: var(--fg-3);
  }

  .pp-cp-footer {
    display: flex; align-items: center; gap: 14px;
    padding: 8px 14px;
    border-top: 1px solid var(--border-1);
    background: var(--bg-2);
  }
  .pp-cp-hint {
    display: flex; align-items: center; gap: 6px;
    font-size: 10.5px;
    color: var(--fg-2);
  }
  .pp-cp-brand {
    display: flex; align-items: center; gap: 5px;
  }
  `;
  document.head.appendChild(s);
}

export { CommandPalette };
